import { createSonarSelector } from '@lib/selectors';
import { useAction } from './useAction';
import { useCallback } from 'react';
import { useSonarSelector } from './useSonarSelector';

import { coreActions, SalesforceMetadata } from '@sonar-software/sonar-core';

const sonarObjectOptimisiticSelector = createSonarSelector((state) => state.core.sonarObjects.optimistic);
const sonarObjectRefsSelector = createSonarSelector((state) => state.core.sonarObjects.byIdMap);

export type OptimisticUpdateOptions = {
	changes: Partial<SalesforceMetadata>;
	sonarObjectIds: number[];
};

export type UseOptimisticUpdateResult = {
	changes: Partial<SalesforceMetadata>;
	clear: () => void;
};

export function useOptimisticUpdate(sonarObjectId?: number): UseOptimisticUpdateResult {
	const refreshSalesforceData = useAction(coreActions.requestPostCoreSonarObjectsSalesforceUpdateable);
	const removeOptimisticChanges = useAction(coreActions.removeSonarObjectsOptimisticChange);
	const optimisticChanges = useSonarSelector((state) => sonarObjectOptimisiticSelector(state).data);
	const sonarObjectRefs = useSonarSelector((state) => sonarObjectRefsSelector(state).data);

	const clear = useCallback(() => {
		const sonarObjectIds = Object.keys(optimisticChanges?.sonarObjectIds ?? {}).map(Number)
			.filter((id) => sonarObjectRefs?.[ id ]);

		refreshSalesforceData({ sonarObjectIds });

		setTimeout(removeOptimisticChanges, 1000);
	}, [ optimisticChanges?.sonarObjectIds, refreshSalesforceData, removeOptimisticChanges, sonarObjectRefs ]);

	const hasChange = (sonarObjectId && optimisticChanges?.sonarObjectIds[ sonarObjectId ]) ?? false;
	const changes = hasChange ? optimisticChanges?.salesforce ?? {} : {};

	return {
		changes,
		clear
	};
}
